// =============================================================
// SCREENS 3 — Status / Jeux / Arena — données réelles
// =============================================================
import React, { useState, useMemo, useEffect } from 'react';
import { Icon, SecHead, Spinner, ErrorState, EmptyState, ItemImg } from './components';
import {
  allChamps, getChamp, getItem, champCentered, champLoading, champSquare,
} from './ddragon';
import { useApi } from './useApi';
import { btrApi } from './api';
import { REGIONS, regionName, itemTagFr, normalize, matchesQuery } from './format';

// ============================================================
// STATUS — État des serveurs (Riot platform-data par région)
// ============================================================
// Une maintenance *programmée* (annonce de patch) n'est pas une panne : seule une
// maintenance en cours ou un incident critique retire la région des « opérationnelles ».
// Les annonces réservées aux applis mobiles (Riot Mobile) ne changent pas l'état du jeu PC.
const concernsPc = (a) => !a.platforms?.length || a.platforms.some((p) => p === 'windows' || p === 'macos');
function statusOf(d) {
  if (!d) return 'unknown';
  const maint = (d.maintenances || []).filter(concernsPc);
  const inc = (d.incidents || []).filter(concernsPc);
  if (maint.some((m) => m.maintenance_status === 'in_progress')) return 'maintenance';
  if (inc.some((i) => i.incident_severity === 'critical')) return 'degraded';
  if (inc.some((i) => i.incident_severity === 'warning')) return 'warning';
  return 'operational';
}
const isUp = (st) => st === 'operational' || st === 'warning';
const STATUS_LABEL = { operational: 'Stable', warning: 'Avertissement', maintenance: 'Maintenance', degraded: 'Incident', unknown: 'Injoignable' };
const STATUS_COLOR = { operational: 'var(--win)', warning: 'var(--gold)', maintenance: 'var(--info)', degraded: 'var(--loss)', unknown: 'var(--ink-3)' };
const STATUS_SOFT = { operational: 'var(--win-soft)', warning: 'rgba(244,194,77,0.14)', maintenance: 'rgba(96,165,250,0.14)', degraded: 'var(--loss-soft)', unknown: 'var(--surface-2)' };
const SEVERITY_FR = { info: 'Info', warning: 'Avertissement', critical: 'Incident' };
const PLATFORM_FR = { windows: 'Windows', macos: 'macOS', ios: 'iOS', android: 'Android' };
// Titre d'une annonce Riot dans la bonne langue (Riot renvoie toutes les locales, dans un ordre variable)
const pickText = (arr) => (arr || []).find((t) => t.locale === 'fr_FR')?.content
  || (arr || []).find((t) => t.locale === 'en_US')?.content || arr?.[0]?.content || null;

function StatusScreen() {
  const all = useApi('status-all', () => Promise.all(
    REGIONS.map((r) => btrApi.serverStatus(r.platform)
      .then((d) => ({ region: r.code, platform: r.platform, data: d }))
      .catch((e) => ({ region: r.code, platform: r.platform, error: e })))
  ));
  const [sel, setSel] = useState('EUW');

  const rows = all.data || [];
  const counts = rows.reduce((acc, r) => {
    const st = r.error ? 'unknown' : statusOf(r.data);
    acc[st] = (acc[st] || 0) + 1; return acc;
  }, {});
  const op = rows.filter((r) => !r.error && isUp(statusOf(r.data))).length;
  const selRow = rows.find((r) => r.region === sel);
  const selIncidents = selRow && !selRow.error
    ? [...(selRow.data.maintenances || []), ...(selRow.data.incidents || [])] : [];

  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 32}}>
        <div className="eyebrow">État des serveurs · données officielles Riot</div>
        <h1 className="h1" style={{marginTop:10}}>
          {rows.length ? op : "…"} région{op > 1 ? "s" : ""} sur {REGIONS.length} <span style={{color:"var(--accent)"}}>opérationnelle{op > 1 ? "s" : ""}</span>
        </h1>
        <p className="lead" style={{marginTop:14, maxWidth: 680}}>
          Maintenances en cours, maintenances programmées et incidents, région par région. Une maintenance
          programmée est une simple annonce : le jeu reste accessible.
        </p>
      </header>

      {all.loading && !rows.length ? <Spinner label="Interrogation des serveurs Riot…"/> :
       all.error ? <ErrorState error={all.error} onRetry={all.refetch}/> : (
      <>
        <section className="g-4 stagger kpis" style={{marginBottom: 36}}>
          <StatusKpi label="Stables" value={counts.operational || 0} color="var(--win)" pip/>
          <StatusKpi label="Avertissements" value={counts.warning || 0} color="var(--gold)" pip/>
          <StatusKpi label="En maintenance" value={counts.maintenance || 0} color="var(--info)" pip/>
          <StatusKpi label="Incidents" value={(counts.degraded || 0) + (counts.unknown || 0)} color="var(--loss)" pip/>
        </section>

        <div className="g-2" style={{alignItems:"start", marginBottom: 36}}>
          <section>
            <SecHead title="Toutes les régions" meta="Cliquer pour le détail"/>
            <div className="card card-flush">
              {rows.map((r, i) => {
                const st = r.error ? 'unknown' : statusOf(r.data);
                const scheduled = !r.error && (r.data.maintenances || []).some((m) => m.maintenance_status === 'scheduled');
                const n = r.error ? 0 : (r.data.maintenances?.length || 0) + (r.data.incidents?.length || 0);
                const sub = r.error ? "Statut indisponible"
                  : st === 'operational' && scheduled ? "Maintenance programmée, jeu accessible"
                  : n ? `${n} annonce${n > 1 ? "s" : ""} en cours` : "Aucune annonce";
                return (
                  <button key={r.region} className={"sr " + (sel === r.region ? "active" : "")}
                          onClick={() => setSel(r.region)}
                          style={{borderBottom: i < rows.length-1 ? "1px solid var(--border)" : "none"}}>
                    <span className="sr-pip" style={{background: STATUS_COLOR[st], boxShadow: `0 0 0 3px ${STATUS_SOFT[st]}`}}/>
                    <div className="grow" style={{textAlign:"left"}}>
                      <div className="row-tight" style={{gap:10}}>
                        <span style={{fontWeight:700,fontSize:14,letterSpacing:"0.04em"}}>{r.region}</span>
                        <span className="tag" style={{background: STATUS_SOFT[st], color: STATUS_COLOR[st]}}>{STATUS_LABEL[st]}</span>
                      </div>
                      <div className="muted" style={{fontSize:12,marginTop:3}}>{sub}</div>
                    </div>
                    <div className="muted" style={{fontSize:12,textAlign:"right"}}>{regionName(r.region)}</div>
                  </button>
                );
              })}
            </div>
            <style>{`
              .sr { display: flex; align-items: center; gap: 14px; padding: 14px 18px; background: transparent; width: 100%; transition: background .12s; }
              .sr:first-child { border-top-left-radius: 16px; border-top-right-radius: 16px; }
              .sr:last-child { border-bottom-left-radius: 16px; border-bottom-right-radius: 16px; }
              .sr:hover { background: var(--surface-2); } .sr.active { background: var(--accent-soft); }
              .sr-pip { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
            `}</style>
          </section>

          <section>
            <SecHead title={`Annonces · ${regionName(sel)}`} meta={`${selIncidents.length} annonce${selIncidents.length > 1 ? "s" : ""}`}/>
            <div className="stack">
              {selIncidents.length === 0 ? <EmptyState icon="check" title="Tout va bien" hint={`Aucune annonce Riot pour ${regionName(sel)}.`}/> :
               selIncidents.map((inc, i) => {
                const title = pickText(inc.titles) || pickText(inc.updates?.[0]?.translations) || "Annonce Riot";
                const detail = pickText(inc.updates?.[0]?.translations);
                const kind = inc.maintenance_status
                  ? (inc.maintenance_status === 'in_progress' ? 'maintenance' : 'scheduled')
                  : (inc.incident_severity === 'critical' ? 'degraded' : inc.incident_severity === 'warning' ? 'warning' : 'info');
                const label = inc.maintenance_status
                  ? (inc.maintenance_status === 'in_progress' ? 'Maintenance en cours' : inc.maintenance_status === 'complete' ? 'Maintenance terminée' : 'Maintenance programmée')
                  : (SEVERITY_FR[inc.incident_severity] || 'Info');
                const color = kind === 'maintenance' ? 'var(--info)' : kind === 'degraded' ? 'var(--loss)' : kind === 'warning' ? 'var(--gold)' : 'var(--ink-3)';
                const soft = kind === 'maintenance' ? 'rgba(96,165,250,0.14)' : kind === 'degraded' ? 'var(--loss-soft)' : kind === 'warning' ? 'rgba(244,194,77,0.14)' : 'var(--surface-2)';
                const when = new Date(inc.updated_at || inc.created_at || Date.now());
                return (
                  <div key={i} className="card" style={{padding: 18, borderLeft: `3px solid ${color}`}}>
                    <div className="row-bw" style={{marginBottom:8}}>
                      <span className="tag" style={{background: soft, color}}>{label}</span>
                      <span className="muted" style={{fontSize:11}}>{Number.isNaN(when.getTime()) ? "" : when.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <h3 className="h4" style={{fontSize:16}}>{title}</h3>
                    {detail && detail !== title && <p className="muted" style={{fontSize:13,marginTop:6,marginBottom:0,lineHeight:1.5}}>{detail}</p>}
                    {inc.platforms?.length ? <p className="muted" style={{fontSize:12,marginTop:6,marginBottom:0}}>Plateformes : {inc.platforms.map((p) => PLATFORM_FR[p] || p).join(", ")}</p> : null}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </>)}
    </div>
  );
}

function StatusKpi({ label, value, color, pip }) {
  return (
    <div className="kpi">
      <div className="lbl" style={{display:"flex",alignItems:"center",gap:8}}>
        {pip && <span style={{width:8,height:8,borderRadius:"50%",background:color,boxShadow:`0 0 0 3px ${color}33`}}/>}
        {label}
      </div>
      <div className="val" style={{color: color || "var(--ink)"}}>{value}</div>
    </div>
  );
}

// ============================================================
// JEUX — Hub + modes Infini
//  · Skin : moteur anti-triche /api/splash (images croppées côté serveur)
//  · Objet : ddragon (client-side)
// ============================================================
function GuesserScreen() {
  const [game, setGame] = useState("hub");
  if (game === "skin") return <SkinInfinite setGame={setGame}/>;
  if (game === "item") return <ItemInfinite setGame={setGame}/>;
  return <GuesserHub setGame={setGame}/>;
}

function GuesserHub({ setGame }) {
  const skinLb = useApi('skin-inf-lb', () => btrApi.skinScores());
  const itemLb = useApi('item-inf-lb', () => btrApi.itemScores());
  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 36}}>
        <div className="eyebrow">Jeux · Mode Infini</div>
        <h1 className="h1" style={{marginTop:10}}>Enchaîne les devinettes, <span style={{color:"var(--accent)"}}>bats ton record</span>.</h1>
        <p className="lead" style={{marginTop:14, maxWidth: 680}}>
          Devine un maximum de champions (ou d'objets) à la suite. La série continue tant que tu as des essais,
          et ton meilleur score peut rejoindre le classement.
        </p>
      </header>

      <div className="g-2 stagger" style={{marginBottom: 40}}>
        <PuzzleCard title="Splash — Infini" subtitle="Reconnais le champion à partir d'un morceau de splash art zoomé. 5 essais par image, l'image se dézoome à chaque erreur." cta="Jouer" champ="Jhin" onClick={() => setGame("skin")}/>
        <PuzzleCard title="Objet — Infini" subtitle="Retrouve l'objet à partir de son prix, de ses catégories et de son initiale. 3 vies." cta="Jouer" champ="Jinx" onClick={() => setGame("item")}/>
      </div>

      <div className="g-2" style={{alignItems:"start"}}>
        <LeaderboardCard title="Top Splash Infini" state={skinLb}/>
        <LeaderboardCard title="Top Objet Infini" state={itemLb}/>
      </div>
    </div>
  );
}

function LeaderboardCard({ title, state }) {
  const list = Array.isArray(state.data) ? state.data : [];
  return (
    <section>
      <SecHead title={title} meta="Meilleurs scores"/>
      {state.loading && !list.length ? <Spinner/> :
       !list.length ? <EmptyState title="Classement vide" hint="Sois le premier à y inscrire un score."/> : (
        <div className="card card-flush">
          {list.slice(0,10).map((p,i) => (
            <div key={p.player+i} style={{display:"grid",gridTemplateColumns:"24px 1fr auto",gap:14,padding:"14px 18px",borderBottom: i<list.length-1?"1px solid var(--border)":"none"}}>
              <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:18,color:i<3?"var(--accent)":"var(--ink-3)",letterSpacing:"-0.04em"}}>{i+1}</span>
              <div style={{fontWeight:700,fontSize:14}}>{p.player}</div>
              <div className="mono" style={{fontWeight:700,fontSize:14,color:"var(--accent)"}}>{p.score}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PuzzleCard({ title, subtitle, cta, champ, onClick, disabled }) {
  return (
    <button className="puzzle" onClick={onClick} disabled={disabled}>
      <div style={{position:"relative",aspectRatio:"16/7",overflow:"hidden"}}>
        <img src={champCentered(champ)} alt="" onError={e => e.target.src = champSquare(champ)}
             style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform .6s ease"}}/>
        <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg, transparent 0%, rgba(11,12,16,0.95) 100%)"}}/>
      </div>
      <div style={{padding:18}}>
        <h3 className="h4" style={{fontSize:18}}>{title}</h3>
        <p className="body" style={{fontSize:13,marginTop:6,marginBottom:14}}>{subtitle}</p>
        <span className={"btn " + (disabled ? "" : "primary") + " sm"} style={{pointerEvents:"none"}}>{cta} <Icon name="arrowR" size={12}/></span>
      </div>
      <style>{`
        .puzzle { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 0; overflow: hidden; text-align: left; font-family: var(--font-body); color: var(--ink); cursor: pointer; transition: all .2s ease; }
        .puzzle:not(:disabled):hover { transform: translateY(-4px); border-color: var(--accent); box-shadow: 0 16px 40px rgba(0,0,0,0.3); }
        .puzzle:not(:disabled):hover img { transform: scale(1.05); }
        .puzzle:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </button>
  );
}

// ---------- Skin Infini (moteur /api/splash, anti-triche) ----------
function SkinInfinite({ setGame }) {
  const MAX_ERR = 5;
  const champNames = useMemo(() => allChamps().map(c => c.name).sort(), []);
  // Nom saisi -> id ddragon, tolérant aux accents, apostrophes et casse ("kogmaw", "Séraphine")
  const nameToId = useMemo(() => {
    const m = {}; allChamps().forEach(c => { m[normalize(c.name)] = c.id; m[normalize(c.id)] = c.id; }); return m;
  }, []);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading'); // loading|playing|revealed|over|error
  const [errors, setErrors] = useState(0);
  const [score, setScore] = useState(0);
  const [guess, setGuess] = useState('');
  const [answer, setAnswer] = useState(null);
  const [anchor, setAnchor] = useState({ ax: 600, ay: 360 });
  const [nonce, setNonce] = useState(0);
  const [pseudo, setPseudo] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);
  const [flash, setFlash] = useState(null); // { text } après une mauvaise réponse
  const [shake, setShake] = useState(0);

  const newAnchor = () => setAnchor({ ax: 300 + Math.floor(Math.random()*600), ay: 180 + Math.floor(Math.random()*360) });

  const start = async () => {
    setStatus('loading'); setErr(null);
    try {
      const s = await btrApi.splashStart('unlimited');
      setSession(s.sessionId); setScore(0); setErrors(0); setAnswer(null);
      setGuess(''); setSubmitted(false); setPseudo(''); setFlash(null); newAnchor(); setNonce(n => n+1);
      setStatus('playing');
    } catch (e) { setErr(e); setStatus('error'); }
  };
  useEffect(() => { start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tailles alignées sur api/splash.js (384→768 px) : le serveur en déduit le nombre d'erreurs
  const imgUrl = session ? btrApi.splashImg(session, {
    w: 384 + errors * 77, h: 256 + errors * 51, ax: anchor.ax, ay: anchor.ay,
    reveal: status === 'playing' ? undefined : 1, _: nonce + '-' + errors + '-' + status,
  }) : null;

  const submitGuess = async (e) => {
    e.preventDefault();
    if (!guess || status !== 'playing') return;
    const id = nameToId[normalize(guess)] || guess.trim();
    const label = (getChamp(id) || {}).name || guess.trim();
    try {
      const r = await btrApi.splashGuess(session, id, 'champion');
      if (r.correct) { setScore(s => s+1); setAnswer(r.answer); setStatus('revealed'); setFlash(null); }
      else {
        setErrors(r.errors);
        const remaining = Math.max(0, MAX_ERR - r.errors);
        setShake(k => k + 1);
        if (r.gameOver) { setAnswer(r.answer); setStatus('over'); setFlash(null); }
        else setFlash({ text: `Raté, ce n'est pas ${label}. L'image se dézoome : ${remaining} essai${remaining > 1 ? 's' : ''} restant${remaining > 1 ? 's' : ''}.` });
      }
      setGuess('');
    } catch (e2) { setErr(e2); }
  };

  const next = async () => {
    try { await btrApi.splashNext(session); setErrors(0); setAnswer(null); setGuess(''); setFlash(null); newAnchor(); setNonce(n => n+1); setStatus('playing'); }
    catch (e) { setErr(e); }
  };

  const submitScore = async () => {
    if (!pseudo.trim()) return;
    try { await btrApi.submitSkin(pseudo.trim(), score); setSubmitted(true); } catch (e) { setErr(e); }
  };

  const left = Math.max(0, MAX_ERR - errors);
  return (
    <div className="container anim-fade-in">
      <button className="btn ghost sm" onClick={() => setGame('hub')} style={{marginBottom:14}}><Icon name="arrowL" size={14}/> Retour aux jeux</button>
      <header style={{marginBottom: 20}}>
        <div className="row-bw" style={{flexWrap:'wrap',gap:14}}>
          <div>
            <div className="eyebrow">Splash — Infini</div>
            <h1 className="h1" style={{marginTop:8,fontSize:'clamp(32px,5vw,52px)'}}>Quel <span style={{color:'var(--accent)'}}>champion</span> ?</h1>
          </div>
          <div className="row" style={{gap:24}}>
            <div style={{textAlign:'center'}}><div className="bignum t-accent" style={{fontSize:36}}>{score}</div><div className="muted" style={{fontSize:12}}>score</div></div>
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:20,letterSpacing:2}}>{'●'.repeat(left)}<span style={{opacity:0.2}}>{'●'.repeat(MAX_ERR-left)}</span></div>
              <div className="muted" style={{fontSize:12}}>essais (cette image)</div>
            </div>
          </div>
        </div>
      </header>

      {status === 'loading' ? <Spinner label="Préparation…"/> :
       status === 'error' ? <ErrorState error={err} onRetry={start}/> : (
      <div className="g-2" style={{alignItems:'start'}}>
        <div key={'img-' + shake} className={"card card-flush" + (shake ? " shake" : "")} style={{overflow:'hidden',position:'relative',aspectRatio:'3/2',background:'var(--surface-3)'}}>
          {imgUrl && <img key={imgUrl} src={imgUrl} alt="Morceau de splash art à deviner" style={{width:'100%',height:'100%',objectFit:'cover'}}
                          onError={(e)=>{e.currentTarget.style.opacity='0.25';}}/>}
        </div>

        <div>
          {status === 'over' ? (
            <div className="card" style={{textAlign:'center'}}>
              <div className="h3" style={{color:'var(--loss)'}}>Partie terminée</div>
              <p className="muted" style={{marginTop:6}}>C'était <strong style={{color:'var(--ink)'}}>{(getChamp(answer && answer.champion)||{}).name || (answer && answer.champion)}</strong>{answer && answer.skinName ? ' — ' + answer.skinName : ''}.</p>
              <div className="bignum t-accent" style={{fontSize:48,marginTop:10}}>{score}</div>
              <div className="muted" style={{fontSize:12,marginBottom:16}}>champion{score > 1 ? 's' : ''} deviné{score > 1 ? 's' : ''}</div>
              {submitted ? <div className="tag win" style={{margin:'0 auto'}}>Score enregistré ✓</div> : score < 1 ? (
                <p className="muted" style={{fontSize:12}}>Marque au moins 1 point pour entrer au classement.</p>
              ) : (
                <div className="row" style={{gap:10,justifyContent:'center'}}>
                  <div className="field" style={{padding:'10px 14px'}}><input value={pseudo} onChange={e=>setPseudo(e.target.value)} placeholder="Ton pseudo" maxLength={32}/></div>
                  <button className="btn" onClick={submitScore} disabled={!pseudo.trim()}>Enregistrer</button>
                </div>
              )}
              <div style={{marginTop:16}}><button className="btn primary lg" onClick={start}><Icon name="refresh" size={16}/> Rejouer</button></div>
            </div>
          ) : status === 'revealed' ? (
            <div className="card" style={{textAlign:'center'}}>
              <div className="h3" style={{color:'var(--win)'}}>Bien vu !</div>
              <p className="muted" style={{marginTop:6}}><strong style={{color:'var(--ink)'}}>{(getChamp(answer && answer.champion)||{}).name || (answer && answer.champion)}</strong>{answer && answer.skinName ? ' — ' + answer.skinName : ''}</p>
              <div style={{marginTop:16}}><button className="btn primary lg" onClick={next}>Image suivante <Icon name="arrowR" size={16}/></button></div>
            </div>
          ) : (
            <div className="card">
              <div className="field-label">Ta réponse</div>
              <form className="row" style={{gap:10}} onSubmit={submitGuess}>
                <div className="field" style={{flex:1,padding:'12px 16px'}}>
                  <input list="skin-champs" value={guess} onChange={e=>setGuess(e.target.value)} placeholder="Nom du champion…" style={{fontSize:15}} autoFocus/>
                  <datalist id="skin-champs">{champNames.map(n => <option key={n} value={n}/>)}</datalist>
                </div>
                <button className="btn primary" disabled={!guess}>Valider</button>
              </form>
              {flash
                ? <p style={{fontSize:13,marginTop:10,marginBottom:0,color:'var(--loss)',fontWeight:600}}>{flash.text}</p>
                : <p className="muted" style={{fontSize:12,marginTop:10,marginBottom:0}}>À chaque erreur, l'image se dézoome un peu. {left} essai{left>1?'s':''} restant{left>1?'s':''}.</p>}
            </div>
          )}
          {err && status!=='error' && <p className="t-loss" style={{fontSize:12,marginTop:10}}>{String((err && err.message) || err)}</p>}
        </div>
      </div>)}
    </div>
  );
}

// ---------- Objet Infini (ddragon, client-side) ----------
function ItemInfinite({ setGame }) {
  const pool = useMemo(() => {
    const out = []; const seen = new Set();
    for (let i = 1000; i < 7200; i++) {
      const it = getItem(i);
      if (it && it.gold >= 1300 && !it.boots && !seen.has(it.name)) { seen.add(it.name); out.push(it); }
    }
    return out;
  }, []);
  const names = useMemo(() => pool.map(i => i.name).sort(), [pool]);
  const rand = () => pool[Math.floor(Math.random() * pool.length)] || null;

  const [target, setTarget] = useState(rand);
  const [guess, setGuess] = useState('');
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [status, setStatus] = useState('playing'); // playing|over
  const [flash, setFlash] = useState(null); // {ok, name}
  const [pseudo, setPseudo] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);

  const submitGuess = (e) => {
    e.preventDefault();
    if (!guess || status !== 'playing' || !target) return;
    const ok = normalize(guess) === normalize(target.name);
    if (ok) {
      setScore(s => s + 1); setFlash({ ok: true, name: target.name, id: target.id });
      setTarget(rand());
    } else {
      const l = lives - 1; setLives(l); setFlash({ ok: false, name: target.name, id: target.id });
      if (l <= 0) { setStatus('over'); } else { setTarget(rand()); }
    }
    setGuess('');
  };
  const start = () => { setTarget(rand()); setScore(0); setLives(3); setStatus('playing'); setGuess(''); setPseudo(''); setSubmitted(false); setFlash(null); };
  const submitScore = async () => { if (!pseudo.trim()) return; try { await btrApi.submitItem(pseudo.trim(), score); setSubmitted(true); } catch (e) { setErr(e); } };

  return (
    <div className="container anim-fade-in">
      <button className="btn ghost sm" onClick={() => setGame('hub')} style={{marginBottom:14}}><Icon name="arrowL" size={14}/> Retour aux jeux</button>
      <header style={{marginBottom: 20}}>
        <div className="row-bw" style={{flexWrap:'wrap',gap:14}}>
          <div>
            <div className="eyebrow">Objet — Infini</div>
            <h1 className="h1" style={{marginTop:8,fontSize:'clamp(32px,5vw,52px)'}}>Quel <span style={{color:'var(--accent)'}}>objet</span> ?</h1>
          </div>
          <div className="row" style={{gap:24}}>
            <div style={{textAlign:'center'}}><div className="bignum t-accent" style={{fontSize:36}}>{score}</div><div className="muted" style={{fontSize:12}}>score</div></div>
            <div style={{textAlign:'center'}}><div style={{fontSize:20,letterSpacing:2}}>{'●'.repeat(lives)}<span style={{opacity:0.2}}>{'●'.repeat(3-lives)}</span></div><div className="muted" style={{fontSize:12}}>vies</div></div>
          </div>
        </div>
      </header>

      <div className="g-2" style={{alignItems:'start'}}>
        <div className="card" style={{aspectRatio:'1',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:14}}>
          {status === 'over' ? (
            <>
              <div className="bignum t-accent" style={{fontSize:72}}>{score}</div>
              {flash && <div className="row-tight" style={{gap:10}}><ItemImg id={flash.id} size={40}/><span className="muted" style={{fontSize:13}}>Le dernier était <strong style={{color:'var(--ink)'}}>{flash.name}</strong></span></div>}
            </>
          ) : (
            <>
              <div className="bignum" style={{fontSize:140,color:'var(--surface-3)',lineHeight:1}}>?</div>
              <p className="muted" style={{fontSize:13,margin:0,textAlign:'center',maxWidth:260}}>L'image de l'objet est cachée : sers-toi des indices à droite.</p>
            </>
          )}
        </div>
        <div>
          {status === 'over' ? (
            <div className="card" style={{textAlign:'center'}}>
              <div className="h3" style={{color:'var(--loss)'}}>Partie terminée</div>
              <div className="bignum t-accent" style={{fontSize:48,marginTop:10}}>{score}</div>
              <div className="muted" style={{fontSize:12,marginBottom:16}}>objet{score > 1 ? 's' : ''} deviné{score > 1 ? 's' : ''}</div>
              {submitted ? <div className="tag win" style={{margin:'0 auto'}}>Score enregistré ✓</div> : score < 1 ? (
                <p className="muted" style={{fontSize:12}}>Marque au moins 1 point pour entrer au classement.</p>
              ) : (
                <div className="row" style={{gap:10,justifyContent:'center'}}>
                  <div className="field" style={{padding:'10px 14px'}}><input value={pseudo} onChange={e=>setPseudo(e.target.value)} placeholder="Ton pseudo" maxLength={32}/></div>
                  <button className="btn" onClick={submitScore} disabled={!pseudo.trim()}>Enregistrer</button>
                </div>
              )}
              <div style={{marginTop:16}}><button className="btn primary lg" onClick={start}><Icon name="refresh" size={16}/> Rejouer</button></div>
            </div>
          ) : (
            <>
              <div className="card" style={{marginBottom:16}}>
                <SecHead title="Indices"/>
                <table className="tbl"><tbody>
                  <tr><td className="muted">Coût</td><td className="t-gold mono" style={{fontWeight:700}}>{target && target.gold} PO</td></tr>
                  <tr><td className="muted">Catégories</td><td style={{fontWeight:700}}>{((target && target.tags) || []).slice(0,5).map(itemTagFr).join(', ') || '—'}</td></tr>
                  <tr><td className="muted">Initiale</td><td style={{fontWeight:700}}>{((target && target.name) || '?')[0]}…</td></tr>
                </tbody></table>
              </div>
              <div className="card">
                <div className="field-label">Ta réponse</div>
                <form className="row" style={{gap:10}} onSubmit={submitGuess}>
                  <div className="field" style={{flex:1,padding:'12px 16px'}}>
                    <input list="item-names" value={guess} onChange={e=>setGuess(e.target.value)} placeholder="Nom de l'objet…" style={{fontSize:15}} autoFocus/>
                    <datalist id="item-names">{names.map(n => <option key={n} value={n}/>)}</datalist>
                  </div>
                  <button className="btn primary" disabled={!guess}>Valider</button>
                </form>
                {flash && (
                  <div className="row-tight" style={{gap:8,marginTop:12}}>
                    <ItemImg id={flash.id} size={26}/>
                    <span style={{fontSize:13,fontWeight:600,color: flash.ok ? 'var(--win)' : 'var(--loss)'}}>
                      {flash.ok ? `Bien vu, c'était ${flash.name} !` : `Raté, c'était ${flash.name}.`}
                    </span>
                  </div>
                )}
                {err && <p className="t-loss" style={{fontSize:12,marginTop:6}}>{String((err && err.message) || err)}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ARENA — Challenge Top 1 (tous les champions) + tirage de duos
// ============================================================
function loadArenaTarget() {
  try { return JSON.parse(localStorage.getItem('btr-arena-summoner')) || null; } catch { return null; }
}

function ArenaScreen({ region }) {
  const [target, setTarget] = useState(loadArenaTarget);
  const [input, setInput] = useState(target ? `${target.name}#${target.tag}` : "");
  const [scan, setScan] = useState(null); // null | 'starting' | 'running' | 'done' | 'error'
  const [scanInfo, setScanInfo] = useState(null);
  const [seed, setSeed] = useState(0);

  const key = target ? `arena-top1-${target.region}-${target.name}-${target.tag}` : null;
  const state = useApi(key, () => btrApi.arenaTop1({ region: target.region, summoner: `${target.name}#${target.tag}` }));
  const data = state.data;

  const submit = (e) => {
    e.preventDefault();
    const t = input.trim();
    const i = t.indexOf("#");
    if (i <= 0) return;
    const next = { region: region || 'EUW', name: t.slice(0, i).trim(), tag: t.slice(i + 1).trim() };
    setTarget(next);
    setScan(null); setScanInfo(null);
    try { localStorage.setItem('btr-arena-summoner', JSON.stringify(next)); } catch { /* nav privée */ }
  };

  // Scan asynchrone : POST /arena/scan puis polling du statut toutes les 10 s.
  const startScan = async () => {
    if (!target || !data?.puuid) return;
    setScan('starting');
    try {
      await btrApi.arenaScanStart({ region: target.region, summoner: `${target.name}#${target.tag}` });
      setScan('running');
    } catch (e) {
      setScan('error'); setScanInfo({ error: e.message });
    }
  };
  useEffect(() => {
    if (scan !== 'running' || !data?.puuid) return undefined;
    const id = setInterval(async () => {
      try {
        const st = await btrApi.arenaScanStatus(data.puuid);
        if (st.status === 'done') { setScan('done'); setScanInfo(st.summary); state.refetch(); }
        else if (st.status === 'error') { setScan('error'); setScanInfo({ error: st.error }); }
      } catch { /* statut momentanément indisponible, on réessaie */ }
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan, data?.puuid]);

  // Champions faits / restants (matching par championId ddragon)
  const all = useMemo(() => [...allChamps()].sort((a, b) => a.name.localeCompare(b.name, 'fr')), []);
  const doneById = useMemo(() => {
    const m = new Map();
    (data?.top1Champions || []).forEach((c) => m.set(Number(c.championId), c));
    return m;
  }, [data]);
  const remaining = useMemo(() => all.filter((c) => !doneById.has(c.key)), [all, doneById]);
  // Compteur officiel Riot (challenge 602002, all-time) ; le scan ne voit que
  // ~2 ans d'historique match-v5, il sert au détail par champion.
  const official = Math.max(data?.challenge?.value ?? 0, doneById.size);
  const pct = all.length ? Math.round((official / all.length) * 100) : 0;
  const officialRemaining = Math.max(0, all.length - official);

  // Duos tirés parmi les champions RESTANTS du challenge (sinon tous)
  const duoPool = remaining.length >= 8 ? remaining : all;
  const duos = useMemo(() => {
    const arr = [...duoPool].sort(() => Math.random() - 0.5);
    return [[arr[0],arr[1]],[arr[2],arr[3]],[arr[4],arr[5]],[arr[6],arr[7]]].filter(d => d[0] && d[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, duoPool.length, data]);

  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 28}}>
        <div className="eyebrow">Arena · Challenge Top 1</div>
        <h1 className="h1" style={{marginTop:10}}>Un Top 1 avec <span style={{color:"var(--accent)"}}>chaque champion</span>.</h1>
        <p className="lead" style={{marginTop:14, maxWidth: 680}}>
          Suis ta progression du challenge Arena : les Top 1 déjà décrochés, les champions restants,
          et des duos tirés au sort parmi ceux qu'il te reste à faire.
        </p>
      </header>

      <div className="card" style={{padding:20, marginBottom:28}}>
        <form className="row" style={{gap:12, flexWrap:"wrap"}} onSubmit={submit}>
          <span className="tag accent" title="Région choisie dans la barre du haut">{region || 'EUW'}</span>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ton Riot ID (Nom#TAG)…" aria-label="Riot ID à suivre"
                 style={{flex:1, minWidth:220, background:"var(--surface-2)", border:"1px solid var(--border)",
                         borderRadius:10, padding:"10px 14px", color:"var(--ink)", fontSize:14, outline:"none"}}/>
          <button className="btn primary" disabled={!input.includes("#")}>Suivre ce joueur</button>
        </form>
      </div>

      {target && state.loading && !data && <Spinner label="Chargement du challenge…"/>}
      {target && state.error && <ErrorState error={state.error} onRetry={state.refetch}/>}

      {data && (
        <>
          <section className="card" style={{padding:24, marginBottom:28}}>
            <div className="row-bw" style={{flexWrap:"wrap", gap:16}}>
              <div>
                <div className="eyebrow">{data.gameName}#{data.tagLine}</div>
                <div className="h2" style={{marginTop:6}}>
                  <span style={{color:"var(--accent)"}}>{official}</span>
                  <span className="muted" style={{fontWeight:500}}> / {all.length} champions en Top 1</span>
                  {data.challenge?.level && <span className="tag accent" style={{marginLeft:10,verticalAlign:"middle"}}>{data.challenge.level}</span>}
                </div>
                <div className="muted" style={{fontSize:12, marginTop:6}}>
                  {data.challenge
                    ? <>Compteur officiel Riot (challenge « Adaptation en toute situation »)
                        {data.challenge.percentile != null && <> · top {Math.max(0.1, +(data.challenge.percentile * 100).toFixed(1))}% des joueurs</>}
                        {doneById.size < official && <> · {doneById.size} identifié{doneById.size > 1 ? 's' : ''} par champion ci-dessous</>}
                      </>
                    : <>Décompte issu du scan BTR.</>}
                  {data.scanCoverage?.oldestGameCreation && (
                    <> · Historique Riot disponible depuis le <strong>{new Date(data.scanCoverage.oldestGameCreation).toLocaleDateString('fr-FR')}</strong> ({data.scanCoverage.totalMatches} parties) : les Top 1 plus anciens comptent dans le total, sans détail par champion.</>
                  )}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <button className="btn outline" onClick={startScan} disabled={scan === 'starting' || scan === 'running'}>
                  <Icon name="refresh" size={14}/>
                  {scan === 'running' || scan === 'starting' ? ' Scan en cours…' : ' Scanner mes nouvelles parties'}
                </button>
                <div className="muted" style={{fontSize:12, marginTop:6}}>
                  {scan === 'running' && 'Analyse des parties Arena via Riot — ça peut prendre quelques minutes.'}
                  {scan === 'done' && scanInfo && `Scan terminé : ${scanInfo.processedMatches} parties analysées, ${scanInfo.newTop1Found} nouveau(x) Top 1.`}
                  {scan === 'error' && <span className="t-loss">Scan échoué : {scanInfo?.error}</span>}
                </div>
              </div>
            </div>
            <div style={{display:"flex", height:10, borderRadius:999, overflow:"hidden", background:"var(--surface-3)", marginTop:18}}>
              <div style={{width:`${pct}%`, background:"var(--accent)", transition:"width .4s"}}/>
            </div>
            <div className="row-bw" style={{fontSize:12, marginTop:8}}>
              <span className="muted">{pct}% du challenge</span>
              <span className="muted">{officialRemaining} restant{officialRemaining > 1 ? 's' : ''}</span>
            </div>
          </section>

          <ChallengeGrid all={all} doneById={doneById}/>
        </>
      )}

      {!data && !target && (
        <EmptyState title="Aucun joueur suivi" hint="Entre ton Riot ID (Nom#TAG) pour suivre ta progression du challenge."/>
      )}

      <section style={{marginTop:40}}>
        <SecHead title={remaining.length >= 8 && data ? "Duos parmi tes champions restants" : "Quatre duos tirés au hasard"}
                 meta={data && remaining.length >= 8 ? `${remaining.length} champions en lice` : "Pour varier les parties Arena"}
                 action={<button className="btn primary" onClick={() => setSeed(s => s + 1)}><Icon name="shuffle" size={16}/> Nouveau tirage</button>}/>
        <div className="g-2 stagger">
          {duos.map((duo, i) => <DuoCard key={i+"-"+seed} duo={duo} idx={i+1}/>)}
        </div>
      </section>
    </div>
  );
}

function ChallengeGrid({ all, doneById }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const list = all.filter((c) => {
    if (filter === "done" && !doneById.has(c.key)) return false;
    if (filter === "todo" && doneById.has(c.key)) return false;
    if (q && !matchesQuery(c.name, q) && !matchesQuery(c.id, q)) return false;
    return true;
  });
  return (
    <section>
      <SecHead title="Tous les champions" meta="En couleur : un Top 1 retrouvé dans tes parties récentes"
               action={
                 <div className="row" style={{gap:10}}>
                   <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…"
                          style={{background:"var(--surface-2)", border:"1px solid var(--border)", borderRadius:8,
                                  padding:"7px 12px", color:"var(--ink)", fontSize:13, outline:"none", width:140}}/>
                   <div className="seg">
                     {[["all","Tous"],["done","Faits"],["todo","Restants"]].map(([id, label]) => (
                       <button key={id} className={filter === id ? "active" : ""} onClick={() => setFilter(id)}>{label}</button>
                     ))}
                   </div>
                 </div>
               }/>
      {list.length === 0 ? <EmptyState title="Aucun champion" hint="Modifie le filtre."/> : (
        <div className="champ-challenge-grid">
          {list.map((c) => {
            const done = doneById.get(c.key);
            return (
              <div key={c.id} className={"cc-cell" + (done ? " done" : "")}
                   title={done
                     ? `${c.name} — ${done.top1Count} Top 1 (dernier : ${done.latestTop1 ? new Date(done.latestTop1).toLocaleDateString('fr-FR') : '?'})`
                     : `${c.name} — pas encore de Top 1`}>
                <img src={champSquare(c.id)} alt={c.name} loading="lazy"/>
                {done && <span className="cc-badge"><Icon name="check" size={10}/>{Number(done.top1Count) > 1 ? ` ×${done.top1Count}` : ''}</span>}
                <span className="cc-name">{c.name}</span>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
        .champ-challenge-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(76px, 1fr)); gap: 10px; }
        .cc-cell { position: relative; text-align: center; }
        .cc-cell img { width: 100%; aspect-ratio: 1; border-radius: 10px; display: block;
                       filter: grayscale(1) brightness(0.55); transition: filter .15s, transform .15s; }
        .cc-cell.done img { filter: none; box-shadow: 0 0 0 2px var(--accent); }
        .cc-cell:hover img { filter: none; transform: scale(1.05); }
        .cc-badge { position: absolute; top: 4px; right: 4px; background: var(--accent); color: var(--accent-text);
                    font-size: 10px; font-weight: 800; padding: 1px 5px; border-radius: 6px;
                    display: inline-flex; align-items: center; gap: 1px; }
        .cc-name { display: block; font-size: 10px; color: var(--ink-3); margin-top: 4px; line-height: 1.2;
                   overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      `}</style>
    </section>
  );
}

function DuoCard({ duo, idx }) {
  return (
    <div className="duo card">
      <div className="row-bw" style={{marginBottom: 16}}>
        <div className="row-tight">
          <span style={{fontFamily:"var(--font-display)",fontWeight:800,fontSize:20,color:"var(--accent)",letterSpacing:"-0.04em"}}>#{String(idx).padStart(2,"0")}</span>
          <span className="muted" style={{fontSize:13}}>Duo</span>
        </div>
      </div>
      <div className="g-2" style={{gap:12}}>
        {duo.map(c => (
          <div key={c.id}>
            <div className="poster-img" style={{aspectRatio:"3/4",borderRadius:12,overflow:"hidden",position:"relative"}}>
              <img src={champLoading(c.id)} alt={c.name} loading="lazy" decoding="async"
                   onError={e => e.target.src = champSquare(c.id)} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            </div>
            <div style={{fontWeight:700,marginTop:10,fontSize:14}}>{c.name}</div>
            <div className="muted" style={{fontSize:11,fontStyle:"italic"}}>{c.title}</div>
          </div>
        ))}
      </div>
      <div className="row" style={{gap:6, marginTop:14, flexWrap:"wrap"}}>
        {[...new Set(duo.flatMap(c => c.tags))].slice(0,4).map((t,i) => <span key={i} className="tag">{({Fighter:'Combattant',Marksman:'Tireur',Support:'Soutien'})[t] || t}</span>)}
      </div>
      <style>{`.duo { transition: all .18s ease; } .duo:hover { border-color: var(--accent); transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,0.25); }`}</style>
    </div>
  );
}

export {
  StatusScreen, GuesserScreen, ArenaScreen,
};
